import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { useApi, type CleaningTaskQueueResponse } from '@/shared/api';
import { cleaningFailure } from '../cleaningFailure';

/** Manuelle Aufgabe anlegen und „Übernehmen & starten“ aus Liste und Detailblatt. */
export function useBoardMutations() {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['cleaning'] });
  const fail = async (error: unknown, fallback: string) =>
    toast.error((await cleaningFailure(error, fallback)).text);

  const create = useMutation({
    mutationFn: (vars: { propertyId: string; scheduledFor: string; notes: string | null }) =>
      api.cleaning.create({ ...vars, bookingId: null }),
    onSuccess: () => {
      toast.success('Aufgabe angelegt');
      setCreateOpen(false);
    },
    onError: async (error) => {
      const failure = await cleaningFailure(error, 'Aufgabe nicht angelegt.');
      toast.error(failure.text);
      // Offen, ob sie angelegt wurde: der Dialog schließt, sonst legte ein zweiter Klick sie doppelt an.
      if (failure.unknown) setCreateOpen(false);
    },
    onSettled: refresh,
    networkMode: 'always',
  });

  // Ohne Netz sind die Knöpfe gesperrt; pausieren ließe sie endlos drehen und später mit altem
  // Stand übernehmen.
  const claim = useMutation({
    networkMode: 'always',
    mutationFn: (task: CleaningTaskQueueResponse) => api.cleaning.claimAndStart(task.id),
    onSuccess: (_task, selected) =>
      void navigate({ to: '/cleaning/$id', params: { id: selected.id } }),
    onError: (error) => fail(error, 'Nicht übernommen.'),
    onSettled: refresh,
  });

  return { create, claim, createOpen, setCreateOpen };
}
